import React from 'react';
import { PermissionType } from '@alucio/aws-beacon-amplify/src/models';
import { DNAButton, DNABox, DNAText, DNACard, DNADivider, Iffy, util } from '@alucio/lux-ui';
import {
  DNAChip,
  DNACardComponent,
  DNACustomDeckContextMenu,
  DNACustomDeckThumbnail,
  DNACommonConfirmation,
  DNAModalActions,
  useDNACustomDeckActions,
  useCurrentPage,
  DNAFolderStatusChip,
} from 'src/components/DNA';
import { useDispatch } from 'src/state/redux';
import { FolderItemORM } from 'src/types/types';
import { isCustomDeckORM } from 'src/types/typeguards';
import { useAppSettings } from 'src/state/context/AppSettings';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';
import { getRootFolderORM } from 'src/state/redux/selector/folder';
import { isSharedEditableFolder } from 'src/utils/foldersHelpers';
import { styles } from './Styles';

export const DNACustomDeckCard: DNACardComponent<FolderItemORM> = (props) => {
  const { ORM: folderItemORM, isOnline, onPress } = props;
  const { deviceMode } = useAppSettings();
  const isDesktop = deviceMode === 'desktop';
  const dispatch = useDispatch()
  const DNACustomDeckActions = useDNACustomDeckActions()

  const route = useCurrentPage({ exact: false })
  const enableCustomDecksCollaborationEdit = useFeatureFlags('enableCustomDecksCollaborationEdit');

  if (!isCustomDeckORM(folderItemORM.relations.itemORM)) { return (<></>) }

  const rootFolder =
    folderItemORM.relations.parentORM
      ? getRootFolderORM(folderItemORM.relations.parentORM)
      : folderItemORM.relations.parentORM

  const customDeckORM = folderItemORM.relations.itemORM
  const {
    assets: { isContentCached },
    version: { requiresReview, withinGracePeriod },
  } = customDeckORM.meta
  const unavailableContent = !isOnline && !isContentCached;

  const openDeckEditor = DNACustomDeckActions.edit(customDeckORM, folderItemORM, 'REVIEW')
  const isSharedFolders = route?.PATH.includes('shared_folders')
  const isSharedEditFolder = rootFolder && isSharedEditableFolder(rootFolder) && enableCustomDecksCollaborationEdit
  const hasEditPermission = rootFolder?.meta.permission === PermissionType.EDIT
  const isReviewAllowed = hasEditPermission &&
    ((!isSharedFolders || isSharedEditFolder) || enableCustomDecksCollaborationEdit) &&
    !withinGracePeriod

  const handleCardPress = (isThumbnail: boolean) => {
    // [TODO-2126] - We should have a property that combines isOnline and isContentCached
    //               on the CustomDeckORM rather than checking both at the component level
    if (!unavailableContent) {
      if (requiresReview && isReviewAllowed) {
        openDeckEditor()
      } else if (!requiresReview || withinGracePeriod) {
        isThumbnail ? DNACustomDeckActions.present(customDeckORM, folderItemORM)() : onPress?.(folderItemORM)
      } else {
        const confirmModal = () => (
          <DNACommonConfirmation
            cancelText="OK"
            title=""
            // eslint-disable-next-line max-len
            descriptionText="This document is currently pending review; only editors may access until the review has been completed."
          />
        )
        const payload = {
          isVisible: true,
          allowBackdropCancel: true,
          component: confirmModal,
        };
        dispatch(DNAModalActions.setModal(payload));
      }
    }
  }

  return (
    <DNACard
      testID="folder-item-custom-deck-card-container"
      appearance="outline"
      interactive="pressable"
      onPress={() => handleCardPress(false)}
      style={util.mergeStyles(
        undefined,
        styles.cardContainerDesktop,
        [styles.cardContainerTablet, !isDesktop],
        [styles.unavailableContent, unavailableContent],
      )}
    >
      {/* ACTION BUTTONS */}
      <DNABox alignX="end">
        <DNACustomDeckContextMenu folderItemORM={folderItemORM}>
          <DNAButton
            appearance="ghost"
            status="subtle"
            size={isDesktop ? 'md' : 'xl'}
            iconLeft="dots-three-vertical-bold"
          />
        </DNACustomDeckContextMenu>
      </DNABox>
      {/* THUMBNAIL & TITLE & CHIPS */}
      <DNABox fill childFill={1} appearance="col" spacing="xs">
        {/* THUMBNAIL */}
        <DNACustomDeckThumbnail
          customDeckORM={customDeckORM}
          width={252}
          height={140}
          unavailableContent={unavailableContent}
          onPress={() => handleCardPress(true)}
          // [TODO] - Implement here - Nonhover
        />
        {/* TITLE */}
        <DNABox fill style={styles.cardTitleContainer}>
          <DNABox fill alignY="center">
            <DNAText testID="document-card-title" style={styles.cardTitle} numberOfLines={2}>
              {folderItemORM.meta.title}
            </DNAText>
          </DNABox>
        </DNABox>
        <DNADivider />
        {/* CHIPS */}
        <DNABox alignY="center" spacing="between" style={styles.cardChipsContainer}>
          <Iffy is={unavailableContent}>
            <DNAChip.Beacon
              testID={`content-not-available-${folderItemORM.meta.title}`}
              variant="CONTENT_UNAVAILABLE"
            />
          </Iffy>
          <Iffy is={!unavailableContent}>
            <DNAChip.Beacon variant="MODIFIED" />
            <DNAFolderStatusChip orm={folderItemORM} variant="List"/>
          </Iffy>
        </DNABox>
      </DNABox>
    </DNACard>
  )
}

export default DNACustomDeckCard
